import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowLeft, User, Shield, Settings as SettingsIcon, Bell } from 'lucide-react';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { useAdminSettings, useFeatureFlagMutation, useMaintenanceMutation } from '../../hooks/useAdminSettings';

export function AdminSettings() {
  const [autoModeration, setAutoModeration] = useState(true);
  const [emailReports, setEmailReports] = useState(true);
  const { data, isLoading, isError } = useAdminSettings();
  const maintenanceMutation = useMaintenanceMutation();
  const flagMutation = useFeatureFlagMutation();
  const [maintenanceMessage, setMaintenanceMessage] = useState('');

  useEffect(() => {
    if (data) {
      setMaintenanceMessage(data.maintenanceMessage);
    }
  }, [data]);

  const handleMaintenanceToggle = (checked: boolean) => {
    maintenanceMutation.mutate(
      { maintenanceMode: checked, maintenanceMessage },
      {
        onSuccess: () => toast.success(`Maintenance mode ${checked ? 'enabled' : 'disabled'}.`),
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Unable to update maintenance mode.'),
      },
    );
  };

  const handleMaintenanceMessageSave = () => {
    maintenanceMutation.mutate(
      { maintenanceMode: data?.maintenanceMode ?? false, maintenanceMessage },
      {
        onSuccess: () => toast.success('Maintenance message updated.'),
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Unable to update message.'),
      },
    );
  };

  const handleFlagToggle = (flagKey: string, enabled: boolean) => {
    const target = data?.featureFlags.find((entry) => entry.key === flagKey);
    if (!target) return;
    flagMutation.mutate(
      { ...target, enabled },
      {
        onSuccess: () => toast.success(`${target.label ?? target.key} ${enabled ? 'enabled' : 'disabled'}.`),
        onError: (error) => toast.error(error instanceof Error ? error.message : 'Unable to update flag.'),
      },
    );
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/admin" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin Dashboard
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="mb-2 text-gold-gradient">
            Admin Settings
          </h1>
          <p className="text-gray-400">Platform configuration and preferences</p>
        </div>

        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger value="general" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <SettingsIcon className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="moderation" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Shield className="w-4 h-4 mr-2" />
              Moderation
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <Bell className="w-4 h-4 mr-2" />
              Notifications
            </TabsTrigger>
            <TabsTrigger value="account" className="data-[state=active]:bg-gold-gradient data-[state=active]:text-black data-[state=active]:text-black">
              <User className="w-4 h-4 mr-2" />
              Account
            </TabsTrigger>
          </TabsList>

          {/* General Tab */}
          <TabsContent value="general">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Platform Settings</CardTitle>
                <CardDescription className="text-gray-400">
                  General platform configuration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-white">Platform Name</Label>
                  <Input
                    defaultValue="Lighthouse Jobs"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Support Email</Label>
                  <Input
                    type="email"
                    defaultValue="support@lighthousejobs.com"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white">Default Job Post Duration (days)</Label>
                  <Select defaultValue="30">
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/10 text-white">
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator className="bg-white/10" />

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white">Maintenance mode</h4>
                      <p className="text-sm text-gray-400">Temporarily pause public traffic while deploying updates.</p>
                    </div>
                    <Switch
                      checked={data?.maintenanceMode ?? false}
                      disabled={isLoading || maintenanceMutation.isPending}
                      onCheckedChange={handleMaintenanceToggle}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Maintenance message</Label>
                    <Input
                      value={maintenanceMessage}
                      onChange={(event) => setMaintenanceMessage(event.target.value)}
                      className="bg-white/10 border-white/20 text-white"
                      disabled={isLoading}
                    />
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      disabled={maintenanceMutation.isPending}
                      onClick={handleMaintenanceMessageSave}
                    >
                      Save message
                    </Button>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Feature flags</h4>
                  {isLoading && <p className="text-sm text-gray-400">Loading feature flags…</p>}
                  {isError && <p className="text-sm text-red-300">Unable to load feature flags.</p>}
                  <div className="space-y-3">
                    {(data?.featureFlags ?? []).map((flag) => (
                      <label key={flag.key} className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{flag.label ?? flag.key}</p>
                          {flag.description && <p className="text-gray-400 text-sm">{flag.description}</p>}
                        </div>
                        <Switch
                          checked={flag.enabled}
                          disabled={flagMutation.isPending}
                          onCheckedChange={(checked) => handleFlagToggle(flag.key, checked)}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Moderation Tab */}
          <TabsContent value="moderation">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Moderation Settings</CardTitle>
                <CardDescription className="text-gray-400">
                  Configure content moderation rules
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Auto-Moderation</p>
                    <p className="text-sm text-gray-400">Automatically flag suspicious content</p>
                  </div>
                  <Switch
                    checked={autoModeration}
                    onCheckedChange={setAutoModeration}
                  />
                </div>

                <Separator className="bg-white/10" />

                <div className="space-y-2">
                  <Label className="text-white">Moderation Sensitivity</Label>
                  <Select defaultValue="medium">
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/10 text-white">
                      <SelectItem value="low">Low - Fewer flags</SelectItem>
                      <SelectItem value="medium">Medium - Balanced</SelectItem>
                      <SelectItem value="high">High - Strict filtering</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Auto-Actions</h4>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">Auto-hide reported posts (3+ reports)</span>
                      <Switch defaultChecked />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">Require verification for new employers</span>
                      <Switch defaultChecked />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">Block spam keywords</span>
                      <Switch defaultChecked />
                    </label>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div className="space-y-2">
                  <Label className="text-white">Blocked Keywords (comma separated)</Label>
                  <Input
                    placeholder="keyword1, keyword2, keyword3"
                    className="bg-white/10 border-white/20 text-white"
                  />
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Save Moderation Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Admin Notifications</CardTitle>
                <CardDescription className="text-gray-400">
                  Configure admin alert preferences
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white">Email Reports</p>
                    <p className="text-sm text-gray-400">Daily summary of new reports</p>
                  </div>
                  <Switch
                    checked={emailReports}
                    onCheckedChange={setEmailReports}
                  />
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Alert Types</h4>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">New user registrations</span>
                      <Switch />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">Content reports</span>
                      <Switch defaultChecked />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">Payment issues</span>
                      <Switch defaultChecked />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-gray-300">System errors</span>
                      <Switch defaultChecked />
                    </label>
                  </div>
                </div>

                <Separator className="bg-white/10" />

                <div className="space-y-2">
                  <Label className="text-white">Report Threshold</Label>
                  <Select defaultValue="3">
                    <SelectTrigger className="bg-white/10 border-white/20 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/10 text-white">
                      <SelectItem value="1">Alert on 1+ reports</SelectItem>
                      <SelectItem value="3">Alert on 3+ reports</SelectItem>
                      <SelectItem value="5">Alert on 5+ reports</SelectItem>
                      <SelectItem value="10">Alert on 10+ reports</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Save Notification Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Admin Account</CardTitle>
                <CardDescription className="text-gray-400">
                  Manage your admin account settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-white">Admin Email</Label>
                    <Input
                      type="email"
                      defaultValue="admin@lighthousejobs.com"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Current Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">New Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Confirm New Password</Label>
                    <Input
                      type="password"
                      className="bg-white/10 border-white/20 text-white"
                    />
                  </div>
                </div>

                <Button className="bg-gold-gradient hover:brightness-110 text-black">
                  Update Password
                </Button>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Admin Team</h4>
                  <p className="text-sm text-gray-400 mb-4">
                    Manage other administrators
                  </p>
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Manage Admin Users
                  </Button>
                </div>

                <Separator className="bg-white/10" />

                <div>
                  <h4 className="text-white mb-4">Activity Log</h4>
                  <div className="space-y-2">
                    {[
                      { action: 'Approved job posting #1234', time: '2 hours ago' },
                      { action: 'Removed reported content', time: '5 hours ago' },
                      { action: 'Updated moderation settings', time: '1 day ago' },
                    ].map((log, idx) => (
                      <div key={idx} className="p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-white text-sm">{log.action}</p>
                        <p className="text-xs text-gray-500">{log.time}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
